import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of welding videos.', 'Not a pile of old shop handouts.', 'Not a text-heavy book you have to turn into lessons.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE CLASS.', 'RUN THE PRACTICE.', 'CHECK THE SKILL.'];
const teachingAreas = [{
  number: '01',
  step: 'WELDING FUNDAMENTALS, SAFETY, EQUIPMENT & METAL PREPARATION',
  detail: 'Arc and electricity basics, power sources, PPE, fume and fire safety, metals, joints, positions and metal prep.'
}, {
  number: '02',
  step: 'MIG/MAG WELDING, GMAW TECHNIQUES & PRACTICAL APPLICATIONS',
  detail: 'Wire feeders, shielding gases, transfer modes, voltage and wire feed speed, gun angles, positions and defects.'
}, {
  number: '03',
  step: 'TIG WELDING, GTAW TECHNIQUES & PRECISION FABRICATION',
  detail: 'Tungsten selection and grinding, AC/DC, torch and filler angles, stainless, aluminum, thin sheet and pulse TIG.'
}, {
  number: '04',
  step: 'STICK WELDING, SMAW PROCESSES & STRUCTURAL WELDING',
  detail: 'Electrode classifications, amperage, arc length, angles, 1F–4F and 1G–4G, open root E6010 and AWS D1.1 basics.'
}, {
  number: '05',
  step: 'WELDING BLUEPRINTS, SYMBOLS, INSPECTION & QUALITY CONTROL',
  detail: 'Print reading, AWS A2.4 welding symbols, WPS/PQR, visual inspection, discontinuities, NDT and bend tests.'
}, {
  number: '06',
  step: 'WELDING INSTRUCTION, WORKSHOP TRAINING & PRACTICAL ASSESSMENTS',
  detail: 'Lesson plans, demonstrations, booth coaching, shop safety, practice projects, rubrics and certificates.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#F3F5F7] relative overflow-hidden" data-id="element-282" data-mp="2how648yl49zi">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#F28A2E]" data-id="element-283" data-mp="2crvxpre9iv58" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="2qir7d5wl5g9v">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="2mhp6kkn0sw5s">
          <p className="text-sm md:text-base font-semibold text-[#B8480A] mb-4" data-id="element-286" data-mp="1tu1slog993s4">
            The Complete Visual Welding Training System for Welding Instructors, Technical Schools &amp; Training Workshops
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#16191E]" data-id="element-287" data-mp="22t6qcsjzst38">
            WELDING INSTRUCTOR <span className="text-[#E8590C]" data-id="element-288" data-mp="3j6ddaktacwzw">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#16191E] font-semibold mb-6" data-id="element-289" data-mp="3e83i4a7k9wea">
            A Complete 6-IN-1 Visual Welding Technology &amp; Technical Training System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3qrzd7flm8edm" />
          <p className="text-base md:text-lg text-[#B8480A] font-semibold mb-10" data-id="element-291" data-mp="3hqj0v2b8q26r">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="eau18elqbngg">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-[#D5DAE0]" data-id="element-293" data-mp="ecf0uimwhwl6">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="x13d6g7bitlc">
                <X className="text-[#16191E]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="34keos42s3z06" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="34iaff6m427si">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#16191E]" data-id="element-297" data-mp="2vjgkdkofhph2">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3j9ax828qze1b">
              <Check className="text-[#E8590C] shrink-0 mt-1" size={20} data-id="element-299" data-mp="3jam44axhp1wn" />
              <div className="space-y-3" data-id="element-300" data-mp="3ehsxfhignjhx">
                <p className="text-[#16191E] font-medium text-base leading-relaxed" data-id="element-301" data-mp="3egq99hcivv5p">
                  WELDING INSTRUCTOR ATLAS is a complete visual training system for welding instructors,
                  technical schools, vocational programs and training workshops — from beginner to
                  advanced.
                </p>
                <p className="text-[#B8480A] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="de5q9ibl1jle">
                  Every page gives you an illustration, labeled diagram or technique sequence, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct technique comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="11buwsiqzrixt">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="11at99essh3z7">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#16191E]" data-id="element-305" data-mp="163cvt19gf9g5">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#16191E]" data-id="element-306" data-mp="1g224q4jdhjk9">
            Make Your Next Welding Class Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="3h2gr1fcn53pi">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="3h0w16319u8xz">
              GET MY COMPLETE 6-IN-1 WELDING TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3l2h714fe1myo">
          <p className="text-[#B8480A] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3ntzdeqhwdbg">
            The System Covers Six Essential Welding Teaching Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="3uwrbhkhlubpr">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-[#D5DAE0]" data-id="element-312" data-mp="8ngejtpvw3mw">
                <span className="text-sm font-bold text-[#B8480A] mb-1" data-id="element-313" data-mp="2ehyek09lraxk">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#16191E]" data-id="element-314" data-mp="2agml1jnly5k3">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="24awz6ojarpz6">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#16191E]" data-id="element-316" data-mp="1qipc5ohtfddo">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}